import type { ReactNode } from 'react';
import { toast } from 'sonner';
import { Copy } from 'lucide-react';
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@staff-app/ui';
import type { StatusVariant } from '../lib/customer-info';

/**
 * The customer identity block — badges, owner, ids, screens, teams — shared by every surface
 * that shows "who is this customer": the Users search card, the customer panel, and the
 * customer Devices page.
 *
 * It exists because the first two carried SEPARATE copies of this markup and drifted, exactly
 * as duplicated UI does: the account id was missing from one, the copy buttons from the other,
 * the font sizes disagreed (12px vs 14px), the badges rendered in a different order, and the
 * spacing under them differed by 6px. Every one of those was reported as a bug. One component
 * makes that class of bug impossible rather than fixed-again.
 *
 * Rows are `<span className="block">` rather than `<div>` on purpose: the panel renders this
 * inside InShellPanelDescription, which is a `<p>`, and a div inside a p is invalid HTML that
 * the browser silently re-parents — which would break the layout only in that one surface.
 *
 * Surface-specific extras stay OUT of here and come in as nodes (`extraBadges`, `teams`,
 * `children`), so this component holds only what all three genuinely share.
 */
export interface CustomerFactsProps {
  /** testid namespace, e.g. `users-search` → `users-search-account-id`. */
  prefix: string;
  /**
   * True where the block sits inside a click target (the search card opens the panel), so a
   * copy click must not also navigate.
   */
  stopPropagation?: boolean;
  /** Reuses accountStatus()'s own return shape — including the `success` variant a
   *  hand-rolled union here would have silently excluded. */
  status: { label: string; variant: StatusVariant };
  plan?: string | null;
  planFeature?: string | null;
  /** Badges only this surface has — the panel's trial-extension count. */
  extraBadges?: ReactNode;
  /**
   * Stripe subscription status, shown as a badge right of the account status. Undefined while
   * it loads or when the customer has no Stripe id — the badge is simply absent rather than
   * guessing "active".
   */
  subscriptionStatus?: string | null;
  /** True when several subscriptions exist and the badge shows the worst of them. */
  subscriptionMultiple?: boolean;
  ownerEmail?: string | null;
  accountId?: string | null;
  stripeId?: string | null;
  pairedScreens?: number | null;
  maxScreens?: number | null;
  /**
   * What the operator searched to reach this customer. Omitted where there is no search
   * context — the Devices page is reached by URL, so "Searched" there would be blank or a
   * restatement of the account id.
   */
  searched?: { value: string; role?: string | null } | null;
  /** Rendered as-is: the card shows team badges, the panel a joined label. */
  teams?: ReactNode;
  /** Extra rows below the shared ones (the card's Add-On strip). */
  children?: ReactNode;
}


/**
 * How each Stripe subscription status reads on the badge.
 *
 * Red means "revenue is not arriving": past_due and unpaid (payment failing) plus canceled
 * (subscription gone). The point of the badge is that those stand out from across the room.
 *
 * COLOUR is not the same as PRIORITY. `canceled` is red but still sorts LAST in
 * STATUS_PRIORITY (stripe-subscription.client.ts), because a customer holding a canceled
 * subscription AND an active one is fine — showing them red would be wrong. Red here says
 * "this is what the worst subscription is", not "this customer is broken".
 *
 * Everything else is informational, and an unrecognised status (Stripe adds them) falls
 * through to a neutral badge showing the raw string rather than being hidden.
 */
const SUBSCRIPTION_BADGE: Record<
  string,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  active: { label: 'Subscription active', variant: 'secondary' },
  trialing: { label: 'Subscription trialing', variant: 'secondary' },
  past_due: { label: 'Subscription past due', variant: 'destructive' },
  unpaid: { label: 'Subscription unpaid', variant: 'destructive' },
  canceled: { label: 'Subscription canceled', variant: 'destructive' },
  incomplete: { label: 'Subscription incomplete', variant: 'outline' },
  incomplete_expired: { label: 'Subscription expired', variant: 'outline' },
};

export function subscriptionBadge(status?: string | null) {
  if (!status) return null;
  return (
    SUBSCRIPTION_BADGE[status] ?? {
      label: `Subscription ${status.replace(/_/g, ' ')}`,
      variant: 'outline' as const,
    }
  );
}

/** One copy-to-clipboard button, identical everywhere it appears (also the Dunning Exclusion List). */
export function CopyBtn({
  value,
  label,
  testId,
  stopPropagation,
}: {
  value: string;
  label: string;
  testId: string;
  stopPropagation?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={async (e) => {
        if (stopPropagation) e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          toast.success(`${label} copied`);
        } catch {
          toast.error('Copy failed');
        }
      }}
      aria-label={`Copy ${label.toLowerCase()}`}
      title={`Copy ${label.toLowerCase()}`}
      data-testid={testId}
      className="ml-1.5 inline-flex cursor-pointer items-center align-middle text-muted-foreground hover:text-foreground"
    >
      <Copy className="h-3 w-3" />
    </button>
  );
}


/**
 * A badge that explains itself on hover.
 *
 * "Account activated" and "Subscription active" sit side by side and both read as a status, so
 * the tooltip is what tells an operator WHICH system each one describes — ours or Stripe's.
 *
 * Provider is local, matching AgentDeviceStatusDot: this component renders in three places and
 * none of their trees mount a TooltipProvider, so owning one here keeps it self-contained.
 * `asChild` means the Badge itself is the trigger — no wrapper element to disturb the badge row
 * layout, and the click still reaches the search card underneath.
 */
function BadgeWithTip({
  tip,
  children,
  ...badge
}: React.ComponentProps<typeof Badge> & { tip: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge {...badge}>{children}</Badge>
        </TooltipTrigger>
        <TooltipContent>{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function CustomerFacts({
  prefix,
  stopPropagation,
  status,
  plan,
  planFeature,
  extraBadges,
  subscriptionStatus,
  subscriptionMultiple,
  ownerEmail,
  accountId,
  stripeId,
  pairedScreens,
  maxScreens,
  searched,
  teams,
  children,
}: CustomerFactsProps) {
  return (
    <>
      {/*
        Plan → feature → status. Spacing from the flex gap, not per-badge margins, so no badge
        carries a stray margin depending on which one renders first. mb-1.5 gives the strip room
        to read as a heading for the facts below rather than as another fact.
      */}
      <span className="mb-1.5 flex flex-wrap items-center gap-1">
        {plan && (
          <Badge variant="secondary" className="text-[10px]">
            {plan}
          </Badge>
        )}
        {planFeature && (
          <Badge variant="outline" className="text-[10px]">
            {planFeature}
          </Badge>
        )}
        <BadgeWithTip
          tip="Account active status"
          variant={status.variant}
          className="text-[10px]"
          data-testid={`${prefix}-status`}
        >
          {status.label}
        </BadgeWithTip>
        {(() => {
          const sub = subscriptionBadge(subscriptionStatus);
          if (!sub) return null;
          return (
            <BadgeWithTip
              tip={
                subscriptionMultiple
                  ? 'Stripe subscription status — this customer has several subscriptions, and the badge shows the one needing attention most.'
                  : 'Stripe subscription status'
              }
              variant={sub.variant}
              className="text-[10px]"
              data-testid={`${prefix}-subscription-status`}
              data-subscription-status={subscriptionStatus ?? undefined}
            >
              {/*
                Label only. A "+" suffix used to mark the several-subscriptions case, but it
                reads as part of the status ("active +"?) rather than as a footnote. The tooltip
                already says it, and that is where a caveat belongs.
              */}
              {sub.label}
            </BadgeWithTip>
          );
        })()}
        {extraBadges}
      </span>

      <span className="block" data-testid={`${prefix}-owner`}>
        <span className="text-foreground">{'Owner: '}</span>
        <span data-testid={`${prefix}-owner-email`}>{ownerEmail ?? '(None)'}</span>
        {/* No button without an owner — copying the literal "(None)" is worse than no button. */}
        {ownerEmail && (
          <CopyBtn
            value={ownerEmail}
            label="Owner email"
            testId={`${prefix}-copy-owner`}
            stopPropagation={stopPropagation}
          />
        )}
      </span>

      {accountId && (
        <span className="block" data-testid={`${prefix}-account-id`}>
          <span className="text-foreground">{'Account id: '}</span>
          <span className="font-mono">{accountId}</span>
          <CopyBtn
            value={accountId}
            label="Account id"
            testId={`${prefix}-copy-account-id`}
            stopPropagation={stopPropagation}
          />
        </span>
      )}

      {stripeId && (
        <span className="block" data-testid={`${prefix}-stripe-id`}>
          <span className="text-foreground">{'Stripe id: '}</span>
          <span className="font-mono">{stripeId}</span>
          <CopyBtn
            value={stripeId}
            label="Stripe id"
            testId={`${prefix}-copy-stripe-id`}
            stopPropagation={stopPropagation}
          />
        </span>
      )}

      {(pairedScreens != null || maxScreens != null) && (
        <span className="flex flex-wrap gap-x-4" data-testid={`${prefix}-screens`}>
          <span>
            <span className="text-foreground">{'Paired Screens: '}</span>
            {pairedScreens ?? '—'}
          </span>
          <span>
            <span className="text-foreground">{'Licensed Screens: '}</span>
            {maxScreens ?? '—'}
          </span>
        </span>
      )}

      {searched && (
        <span className="block">
          <span className="text-foreground">{'Searched: '}</span>
          <span>{searched.value}</span>
          {searched.role && (
            <Badge
              variant="outline"
              className="ml-1.5 align-middle text-[10px]"
              data-testid={`${prefix}-input-role`}
            >
              {searched.role}
            </Badge>
          )}
        </span>
      )}

      {teams && (
        <span className="flex items-center gap-1" data-testid={`${prefix}-teams`}>
          <span className="text-foreground">{'Teams: '}</span>
          {teams}
        </span>
      )}

      {children}
    </>
  );
}
